import {AxiosInstance} from "axios";
import Appointment from "../model/appointment/Appointment";
import {
	CreateUpdateAppointmentApiDto,
	CreateUpdateJobAppointmentApiDto,
	JobAppointmentDetailsApiDto,
	LeadAppointmentDetailsApiDto,
	SalesEngineerApiDto
} from "./model/rest";
import AppointmentType from "../model/appointment/AppointmentType";
import JobAppointment from "../model/appointment/job/JobAppointment";
import JobAppointmentType from "../model/appointment/job/JobAppointmentType";
import AppointmentDetails from "../model/appointment/AppointmentDetails";

export default class AppointmentApi {
	constructor(public readonly axiosInstance: AxiosInstance) { }

	/**
	 * Fetch appointments for given lead
	 * @param {string} leadId
	 * @returns {Promise<Appointment[]>}
	 */
	fetchLeadAppointments(leadId: string): Promise<Appointment[]> {
		const url = `/api/web/leads/${leadId}/appointments`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(Appointment.fromJson));
	}

	/**
	 * Fetch all appointments
	 * @returns {Promise<Appointment[]>}
	 */
	fetchLeadsAppointments(dateFrom: string, dateTo: string): Promise<AppointmentDetails[]> {
		const url = `/api/web/leads/appointments`;
		return this.axiosInstance.get(url, {params: {dateFrom, dateTo}}).then(
			resp => resp.data.map((json: LeadAppointmentDetailsApiDto) => AppointmentDetails.fromJson(json, true))
		);
	}

	/**
	 * Create appointment for lead.
	 * @param {string} leadId
	 * @param {CreateUpdateAppointmentApiDto} data
	 * @returns {Promise<Appointment>}
	 */
	createAppointment(leadId: string, data: CreateUpdateAppointmentApiDto): Promise<Appointment> {
		const url = `/api/web/leads/${leadId}/appointments`;
		return this.axiosInstance.post(url, data).then(resp => Appointment.fromJson(resp.data));
	}

	/**
	 * Update lead appointment.
	 * @param {string} leadId
	 * @param {string} appointmentId
	 * @param {CreateUpdateAppointmentApiDto} data
	 * @returns {AxiosPromise<any>}
	 */
	updateAppointment(leadId: string, appointmentId: string, data: CreateUpdateAppointmentApiDto) {
		const url = `/api/web/leads/${leadId}/appointments/${appointmentId}`;
		return this.axiosInstance.put(url, data);
	}

	/**
	 * Delete appointment
	 * @param {string} appointmentId
	 * @returns {AxiosPromise}
	 */
	deleteAppointment(appointmentId: string) {
		const url = `/api/web/leads/appointments/${appointmentId}`;
		return this.axiosInstance.delete(url);
	}

	/**
	 * Send SMS to prospect with link to the profile url of sales engineer.
	 * @param {string} appointmentId
	 * @param {SalesEngineerApiDto} data
	 * @returns {AxiosPromise<any>}
	 */
	sendLeadAppointmentSalesEngineerLinkSMS(appointmentId: string, data: SalesEngineerApiDto) {
		const url = `/api/web/leads/appointments/${appointmentId}/confirmation_sms`;
		return this.axiosInstance.put(url, data);
	}

	/**
	 * Sends SMS to sales engineer about incoming appointment.
	 * @param {string} appointmentId
	 * @param {SalesEngineerApiDto} data
	 * @return {AxiosPromise<any>}
	 */
	sendAppointmentReminderSMS(appointmentId: string, data: SalesEngineerApiDto) {
		const url = `/api/web/leads/appointments/${appointmentId}/sales_engineer_reminder_sms`;
		return this.axiosInstance.put(url, data);
	}

	/**
	 * Fetch appointment types
	 * @returns {Promise<AppointmentType[]>}
	 */
	fetchAppointmentTypes(): Promise<AppointmentType[]> {
		const url = `/api/web/leads/appointments/types`;
		return this.axiosInstance.get(url).then((res) => res.data.map(AppointmentType.fromJson))
	}

	/**
	 * Fetch jobs appointments
	 * @returns {Promise<JobAppointment[]>}
	 */
	fetchJobsAppointments(dateFrom: string, dateTo: string): Promise<AppointmentDetails[]> {
		const url = `/api/web/jobs/appointments`;
		return this.axiosInstance.get(url, {params: {dateFrom, dateTo}}).then(
			resp => resp.data.map((json: JobAppointmentDetailsApiDto) => AppointmentDetails.fromJson(json, false))
		);
	}

	/**
	 * Fetch appointments for given job
	 * @param {string} jobId
	 * @returns {Promise<JobAppointment[]>}
	 */
	fetchJobAppointments(jobId: string): Promise<JobAppointment[]> {
		const url = `/api/web/jobs/${jobId}/appointments`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(JobAppointment.fromJson));
	}

	/**
	 * Create appointment for job.
	 * @param {string} jobId
	 * @param {CreateUpdateJobAppointmentApiDto} data
	 * @returns {Promise<JobAppointment>}
	 */
	createJobAppointment(jobId: string, data: CreateUpdateJobAppointmentApiDto): Promise<JobAppointment> {
		const url = `/api/web/jobs/${jobId}/appointments`;
		return this.axiosInstance.post(url, data).then(resp => JobAppointment.fromJson(resp.data));
	}

	/**
	 * Update job appointment.
	 * @param {string} jobId
	 * @param {string} appointmentId
	 * @param {CreateUpdateJobAppointmentApiDto} data
	 * @returns {AxiosPromise<any>}
	 */
	updateJobAppointment(jobId: string, appointmentId: string, data: CreateUpdateJobAppointmentApiDto) {
		const url = `/api/web/jobs/${jobId}/appointments/${appointmentId}`;
		return this.axiosInstance.put(url, data);
	}

	/**
	 * Delete job appointment
	 * @param {string} appointmentId
	 * @returns {AxiosPromise}
	 */
	deleteJobAppointment(appointmentId: string) {
		const url = `/api/web/jobs/appointments/${appointmentId}`;
		return this.axiosInstance.delete(url);
	}

	/**
	 * Fetch job appointment types
	 * @returns {Promise<JobAppointmentType[]>}
	 */
	fetchJobAppointmentTypes(): Promise<JobAppointmentType[]> {
		const url = `/api/web/jobs/appointments/types`;
		return this.axiosInstance.get(url).then((res) => res.data.map(JobAppointmentType.fromJson))
	}
}